import { SectionNav } from '@/components/panels/sections/SectionNav/SectionNav';
import { SectionHero } from '@/components/panels/sections/SectionHero/SectionHero';
import { Element } from '@/components/panels/elements/Element/Element';
import { Settings } from '@/components/panels/settings/Settings/Settings';
import { Layers } from '@/components/panels/layers/Layers/Layers';
import { Styles } from '@/components/panels/styles/Styles/Styles';
import { AiBuilder } from '@/components/panels/ai-builder/AiBuilder';
import { Section } from '@/components/panels/sections/Section/Section';
import { Integrations } from '@/components/panels/integrations/Integrations/Integrations';
import clsx from 'clsx';
import Draggable from 'react-draggable';
import { useEffect, useRef, useState } from 'react';
import { setPanelDragging } from '@/store/system/systemSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsFeatureEnabled } from '@/store/featureFlags/selectors';
import { SectionSaved } from '@/components/panels/sections/SectionSaved/SectionSaved';
import { ASSETS_PANEL } from '@builder4/shared/utils/constants';
import type { GlobalStyle } from '@/store/globalStyles/types';
import { StylesEditStyle } from '@/components/panels/styles/StylesEditStyle/StylesEditStyle';
import { PanelErrorFallback } from '@/components/ErrorBoundary';
import * as Sentry from '@sentry/react';
import { EditGlobalStyleModal } from '@/components/panels/styles/EditGlobalStyleModal';
function getLeftPanelDefaultPosition(topOffset: number) {
  const toolset = document.getElementById('sidebar');
  if (toolset) {
    const { top, height } = toolset.getBoundingClientRect();
    return { left: 12, top: top + topOffset, height: height, transform: 'translate(0px,0px)' };
  } else {
    return { left: 12, top: 64, height: window.innerHeight, transform: 'translate(0px,0px)' };
  }
}
const AssetsPanel = ({
  activeItem,
  setActiveItem,
}: {
  activeItem: string;
  setActiveItem: (item: string) => void;
}) => {
  const dispatch = useAppDispatch();
  const isAiBuilderEnabled = useAppSelector(selectIsFeatureEnabled('builder4-ai-builder'));
  const nodeRef = useRef<HTMLDivElement>(null);
  const [hidePanel, setHidePanel] = useState(false);
  const [styleData, setStyleData] = useState<GlobalStyle | null>(null);
  const [styleEditorMode, setStyleEditorMode] = useState<'edit' | 'create'>('edit');
  const [isBrandProfileStyle, setIsBrandProfileStyle] = useState(false);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
    height: number;
    transform: string;
  }>({ left: 0, top: 0, height: 0, transform: '' });

  const handleOnEditStyle = (
    style: GlobalStyle,
    mode: 'edit' | 'create' = 'edit',
    isBrandProfile = false,
  ) => {
    setStyleData(style);
    setStyleEditorMode(mode);
    setIsBrandProfileStyle(isBrandProfile);
  };

  useEffect(() => {
    if (activeItem) {
      setPosition(getLeftPanelDefaultPosition(0));
      // Reset style data when opening for new style creation
      if (activeItem === ASSETS_PANEL.STYLES_NEW) {
        setStyleData(null);
        setStyleEditorMode('create');
        setIsBrandProfileStyle(false);
      }
    }
  }, [activeItem]);

  useEffect(() => {
    if (nodeRef.current) {
      nodeRef.current.style.setProperty(
        '--indicator-x',
        -nodeRef.current.getBoundingClientRect().left.toString() + 'px',
      );
      nodeRef.current.style.setProperty(
        '--indicator-y',
        -nodeRef.current.getBoundingClientRect().top.toString() + 'px',
      );
    }
  }, [nodeRef.current]);

  if (!activeItem) return null;
  return (
    <>
      <Draggable
        onStart={() => {
          dispatch(setPanelDragging(true));
        }}
        onStop={(_, data) => {
          dispatch(setPanelDragging(false));

          nodeRef.current?.style.setProperty(
            '--indicator-x',
            (-data.node.getBoundingClientRect().left).toString() + 'px',
          );
          nodeRef.current?.style.setProperty(
            '--indicator-y',
            (-data.node.getBoundingClientRect().top).toString() + 'px',
          );
        }}
        handle=".assets-drag-handle"
        nodeRef={nodeRef}
        defaultPosition={{ x: 80, y: 0 }}
      >
        <div
          style={{ left: position.left, top: position.top, transform: position.transform }}
          className={clsx(
            'bg-neutral-0 absolute z-100000 h-[calc(100vh-76px)] max-h-[900px] w-full max-w-[280px] overflow-hidden rounded-lg shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-offset-[-1px] outline-neutral-100',
            {
              'opacity-0 transition': hidePanel,
            },
          )}
          ref={nodeRef}
        >
          <Sentry.ErrorBoundary
            key={activeItem}
            fallback={<PanelErrorFallback title="Error loading panel" />}
            showDialog={false}
          >
            {activeItem === ASSETS_PANEL.SECTIONS && (
              <Section className={''} onSectionClick={setActiveItem} />
            )}

            {activeItem === ASSETS_PANEL.SECTION_NAV && (
              <SectionNav className={''} onSectionClick={setActiveItem} />
            )}

            {activeItem === ASSETS_PANEL.SECTION_HERO && (
              <SectionHero className={''} onSectionClick={setActiveItem} />
            )}
            {activeItem === ASSETS_PANEL.SAVED_SECTIONS && (
              <SectionSaved
                className={''}
                onHidePanel={setHidePanel}
                onSectionClick={setActiveItem}
              />
            )}

            {activeItem === ASSETS_PANEL.ELEMENTS && (
              <Element className={''} onHidePanel={setHidePanel} onElementClick={setActiveItem} />
            )}

            {activeItem === ASSETS_PANEL.SETTINGS && (
              <Settings className={''} onSettingsClick={setActiveItem} />
            )}

            {activeItem === ASSETS_PANEL.LAYERS && (
              <Layers className={''} onLayersClick={setActiveItem} />
            )}

            {activeItem === ASSETS_PANEL.STYLES && (
              <Styles
                className={''}
                onStylesClick={setActiveItem}
                onStylesEditClick={handleOnEditStyle}
              />
            )}

            {(activeItem === ASSETS_PANEL.STYLES_NEW ||
              activeItem === ASSETS_PANEL.STYLES_EDIT) && (
              <StylesEditStyle
                className={''}
                onStylesClick={setActiveItem}
                initialData={styleData}
                mode={styleEditorMode}
                isBrandProfileStyle={isBrandProfileStyle}
              />
            )}

            {activeItem === ASSETS_PANEL.INTEGRATIONS && (
              <Integrations className={''} onIntegrationsClick={setActiveItem} />
            )}

            {activeItem === ASSETS_PANEL.AI_BUILDER && isAiBuilderEnabled && (
              <AiBuilder className={''} onClose={() => setActiveItem('')} />
            )}
          </Sentry.ErrorBoundary>
        </div>
      </Draggable>
      {/* Edit Global Style Confirmation Modal*/}
      <EditGlobalStyleModal
        onStylesClick={setActiveItem}
        onStylesEditClick={handleOnEditStyle}
        isBrandProfileStyle={isBrandProfileStyle}
      />
    </>
  );
};

export default AssetsPanel;
